import {GameMode} from "@nolimit/slot-game/bin/flowcontrol/GameMode";
import {GuiButton} from "@nolimit/slot-launcher/bin/gui/buttons/GuiButton";
import {gsap} from "gsap";
import {SlotGame} from "@nolimit/slot-game/bin/SlotGame";
import {NolimitApplication} from "@nolimit/slot-launcher/bin/NolimitApplication";
import {TimelineSprite} from "@nolimit/slot-launcher/bin/animation/TimelineSprite";
import {MathHelper} from "@nolimit/slot-game/bin/utils/MathHelper";
import {OpenSans} from "@nolimit/slot-launcher/bin/loader/font/OpenSans";
import {FontStyle, FontWeight} from "@nolimit/slot-launcher/bin/loader/font/FontStatics";
import {FITH2SoundConfig} from "../configs/FITH2SoundConfig";
import {FITH2SoundManager} from "../sound/FITH2SoundManager";
import {StageLayer} from "@nolimit/slot-launcher/bin/StageLayer";
import {FireInTheHole2} from "../FireInTheHole2";

export class XBetButton extends GuiButton{

    private _state:PIXI.Sprite;

    constructor() {
        super("xBetButton");

        this._state = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures(`xbetbutton/xbet`)[0]);
        this._state.anchor.set(0.5);
        this.addChild(this._state);
        this.hitArea = new PIXI.Rectangle(-50, -38, 100, 70);
    }

    public enable(enable:boolean) {
        super.enable(enable);
    }

    public setState(state:number):void {
        // on
        if(state === 1) {
            this._state.tint = 0xFFbf44;
            // this._state.texture = FireInTheHole2.assetLoader.getTextures(`xbetbutton/On`)[0];
        } else {
            // off
            this._state.tint = 0xffffff;
            // this._state.texture = FireInTheHole2.assetLoader.getTextures(`xbetbutton/Off`)[0];
        }
    }
}

export class XBetController extends PIXI.Container implements StageLayer {

    private _button:XBetButton;
    private _attention:TimelineSprite;
    private _bubble:PIXI.Sprite;
    private _idleAttentionTl:gsap.core.Timeline | null;
    private _bubbleTl:gsap.core.Timeline | null;

    private _boostedData:any;

    private _featureAllowed:boolean;
    private _isFreeBet:boolean;
    private _isIdle:boolean;
    private _bubbleCounter = 0;

    private _buttonsVisible:boolean = false;
    public set buttonsVisible(value:boolean) {
        if(!this._featureAllowed) {
            return;
        }

        if(this._isFreeBet) {
            value = false;
        }
        this._buttonsVisible = value;

        this._button.visible = this._buttonsVisible;
    }

    public get buttonsVisible() {
        return this._buttonsVisible;
    }

    private _featureEnabled:boolean = false;
    public set featureEnabled(value:boolean) {
        if(!this._featureAllowed) {
            return;
        }

        this._featureEnabled = value;
        this._button.enable(this._featureEnabled);
        gsap.to(this._button, {duration : 0.5, alpha : value ? 1 : 0.5});

        if(this._selected && this._buttonsVisible) {
            this._button.setState(1);
        } else {
            if(this._buttonsVisible) {
                this._button.setState(0);
            }
        }
    }

    private _selected:boolean;
    public set selected(value:boolean) {
        if(!this._featureAllowed) {
            return;
        }
        this._selected = value;
        SlotGame.apiPlugin.betHandler.setBoost(this._selected ? this._boostedData : null);
        this.featureEnabled = this._featureEnabled;
        if(this._isIdle) {
            this.startIdle();
        }
    }

    public get selected() {
        return this._selected;
    }

    constructor() {
        super();

        this._featureAllowed = SlotGame.apiPlugin.gameClientConfiguration.boostedBetAllowed;
        if(!this._featureAllowed) {
            return;
        }

        this._button = new XBetButton();
        this._button.y = 15;
        this._button.addClickCallback(() => {
            this.selected = !this.selected;
            FITH2SoundManager.playSound(this.selected ? FITH2SoundConfig.XBET_ON : FITH2SoundConfig.XBET_OFF);
        });
        this.addChild(this._button);
        FireInTheHole2.xBetController?.addChild(this);

        this._attention = new TimelineSprite(FireInTheHole2.assetLoader.getTextures("X_Bet_Attention/X_Bet_Attention_"));
        this._attention.anchor.set(0.5);
        this._attention.blendMode = PIXI.BLEND_MODES.ADD;
        this._attention.position.set(this._button.x, this._button.y);
        this._attention.hide();
        this.addChild(this._attention);

        this._bubble = new PIXI.Sprite(FireInTheHole2.assetLoader.getTextures("xbetbutton/X_Bet_Bubble")[0]);
        this._bubble.anchor.set(0.5, 0.4);
        this._bubble.position.set(-110, -170);
        this.addChild(this._bubble);

        const bubbleText1 = new PIXI.Text(
            FireInTheHole2.apiPlugin.translations.translate("No dirt and 4 times more likely to trigger Lucky Wagon Spins."),
            new PIXI.TextStyle({
                align : "center",
                breakWords : true,
                fill : "#ffffff",
                fontFamily : OpenSans.FAMILY,
                fontSize : 20,
                fontStyle : FontStyle.NORMAL,
                fontWeight : FontWeight.LIGHT,
                wordWrap : true,
                wordWrapWidth : 290
            }));
        bubbleText1.anchor.set(0.5, 0);

        let totalHeight = bubbleText1.height;
        while(totalHeight > 120) {
            bubbleText1.style.fontSize--;
            totalHeight = bubbleText1.height;
        }
        bubbleText1.y = -totalHeight * 0.5;
        this._bubble.addChild(bubbleText1);
        this._bubble.alpha = 0;

        const featureData = SlotGame.apiPlugin.bonusFeatures.getFeatureDataByName(FireInTheHole2.boostedName);
        if(featureData) {
            this._boostedData = {featureName : FireInTheHole2.boostedName, price : featureData.price};
        }

        SlotGame.apiPlugin.freeBets.events.on(SlotGame.apiPlugin.freeBets.FREE_BETS_START, () => this.onFreeBets(true));
        SlotGame.apiPlugin.freeBets.events.on(SlotGame.apiPlugin.freeBets.FREE_BETS_END, () => this.onFreeBets(false));
        this.visible = false;
    }

    public onResize() {
        if(NolimitApplication.isLandscape) {
            this.position.set(360 + NolimitApplication.screenBounds.width * 0.5 - 110, 320);
        } else {
            this.position.set(530, 360 + NolimitApplication.screenBounds.height * 0.5 - 244);
        }
    }

    private onFreeBets(value:boolean):void {
        if(value) {
            this._isFreeBet = true;
            this.selected = false;
            this.buttonsVisible = false;
            this.featureEnabled = false;
        } else {
            this._isFreeBet = false;
            this.buttonsVisible = true;
            this.featureEnabled = true;
        }
    }

    public start():void {
        if(!this._featureAllowed) {
            return;
        }

        if(SlotGame.apiPlugin.freeBets.hasFreeBets()) {
            this._isFreeBet = true;
        }

        this.visible = true;
        this.onResize();
        const nextMode = FireInTheHole2.model.data.nextMode,
        isNormalRestore:boolean = FireInTheHole2.model.isRestore && FireInTheHole2.model.isNormal;
        this.buttonsVisible = (nextMode === GameMode.NORMAL || (isNormalRestore && !FireInTheHole2.model.freespinTriggeredThisSpin));
        this.featureEnabled = nextMode === GameMode.NORMAL;
        this.selected = FireInTheHole2.model.boostedBet;
    }

    public startIdle(now:boolean = false):void {
        if(!this._featureAllowed) {
            return;
        }
        if(!this._buttonsVisible) {
            return;
        }
        if(!this._featureEnabled) {
            return;
        }
        this._isIdle = true;
        if(this._selected) {
            this.stopAllIdleAnimations();
            return;
        }
        if(this._idleAttentionTl && this._idleAttentionTl.isActive()) {
            return;
        }
        this.playIdleAnimation(now);
    }

    private playIdleAnimation(now:boolean):void {
        if(!this._isIdle || !this._buttonsVisible || !this._featureAllowed || !this._featureEnabled || this._selected) {
            return;
        }
        if(now) {
            this._attention.getAnimationAutoShowHide();
            if(this._bubbleCounter === 0) {
                this._bubbleTl = gsap.timeline();
                this._bubbleTl!.add(gsap.to(this._bubble, {alpha : 1, duration : 1}));
                this._bubbleTl!.add(gsap.to(this._bubble, {alpha : 0, duration : 0.8}), 8);
                this._bubbleCounter = 5;
            }
            this._bubbleCounter--;
        }
        this._idleAttentionTl = gsap.timeline();
        this._idleAttentionTl!.add(() => this.playIdleAnimation(true), MathHelper.randomNumberInRange(15, 30, 2));
    }

    public stopIdle():void {
        if(!this._featureAllowed) {
            return;
        }
        this._isIdle = false;
        this.stopAllIdleAnimations();
    }

    private stopAllIdleAnimations():void {

        if(this._idleAttentionTl) {
            this._idleAttentionTl.pause();
            this._idleAttentionTl.kill();
        }
        this._idleAttentionTl = null;
        this._attention.hide();
        if(this._bubbleTl) {
            this._bubbleTl.pause();
            this._bubbleTl.kill();
        }
        this._bubbleTl = null;
        this._bubble.alpha = 0;
    }
}